import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Card, ProgressBar, Form, Spinner, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiagramProject } from '@fortawesome/free-solid-svg-icons';
import AdvanceTable from '../../components/base/AdvanceTable';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { TimeSince } from '../../components/machines/EstimateCell.js';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import Badge from '../../components/base/Badge';
import SearchBox from '../../components/common/SearchBox';
import FilterTab from '../../components/common/FilterTab';
import { API } from '../../config/api';

// Sales view of order reservations: per delivery item — needed vs reserved vs
// produced vs free. Live (5s poll). "Details" opens the full production board.
const API_BASE = `${API.REDIS}/api/redis/orders/reservations`;
const fmt = n => Number(n || 0).toLocaleString();
const U = v => String(v ?? '').toUpperCase();

const specOf = it =>
  it.kind === 'DRUM'
    ? `${it.drumMaterial || ''} ${it.drumDiameter ? 'Ø' + it.drumDiameter : ''}`.trim() ||
      'Drum'
    : 'Coil';

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Cable off the shelf is its own state, not a kind of "reserved".
 *
 * A warehouse reservation is recorded on the order LINE — metres and serials —
 * while this whole screen is built out of delivery items, so until the backend
 * spread it across them an order covered entirely from stock read "Free · 0 / 100"
 * and sat next to genuinely untouched orders looking identical. It is the one
 * state where nothing has to be made at all, which is the opposite of free.
 */
const statusOf = it => {
  const q = num(it.qtyPlanned);
  const stock = num(it.stockReserved);
  const covered = num(it.covered) || Math.max(num(it.reserved), num(it.produced)) + stock;

  if (q > 0 && num(it.produced) >= q)
    return { key: 'produced', label: 'Produced', bg: 'success' };
  if (q > 0 && stock >= q)
    return { key: 'stock', label: 'From stock', bg: 'info' };
  if (q > 0 && covered >= q)
    return { key: 'reserved', label: 'Reserved', bg: 'primary' };
  if (covered > 0) return { key: 'partial', label: 'Partial', bg: 'warning' };
  return { key: 'free', label: 'Free', bg: 'secondary' };
};

const SummaryTile = ({ label, value, bg }) => (
  <Card className="border-0 shadow-sm" style={{ minWidth: 128 }}>
    <Card.Body className="py-2 px-3">
      <div className="text-muted fs-10">{label}</div>
      <Badge bg={bg} variant="phoenix" className="fs-8 mt-1">
        {fmt(value)}
      </Badge>
    </Card.Body>
  </Card>
);

const useColumns = navigate =>
  useMemo(
    () => [
      {
        accessorKey: 'client',
        header: 'Client',
        cell: ({ row: { original } }) => (
          <Badge bg="info" variant="phoenix" className="fs-10">
            {U(original.client) || '—'}
          </Badge>
        ),
        meta: { headerProps: { style: { width: '10%' } } }
      },
      {
        accessorKey: 'orderCode',
        header: 'Order',
        cell: ({ row: { original } }) => (
          <span className="fw-semibold white-space-nowrap">
            {original.orderCode || '—'}
          </span>
        ),
        meta: { headerProps: { style: { width: '12%' } } }
      },
      {
        accessorKey: 'productLabel',
        header: 'Product',
        meta: { headerProps: { style: { width: '12%' } } }
      },
      {
        accessorKey: 'itemLabel',
        header: 'Item',
        cell: ({ row: { original } }) => (
          <span className="white-space-nowrap">
            {original.kind === 'DRUM' ? '🛢 ' : '🎗 '}
            {specOf(original)} · {fmt(original.lengthPerUnit)}m
          </span>
        ),
        meta: { headerProps: { style: { width: '14%' } } }
      },
      {
        accessorKey: 'statusLabel',
        header: 'Status',
        cell: ({ row: { original } }) => {
          const s = statusOf(original);
          return (
            <Badge bg={s.bg} variant="phoenix" className="fs-10">
              {s.label}
            </Badge>
          );
        },
        meta: { headerProps: { style: { width: '9%' }, className: 'text-center' }, cellProps: { className: 'text-center' } }
      },
      {
        id: 'progress',
        header: 'Covered',
        enableSorting: false,
        cell: ({ row: { original: r } }) => {
          const q = num(r.qtyPlanned);
          const stock = num(r.stockReserved);
          const pct = v => (q > 0 ? Math.min(100, (v / q) * 100) : 0);

          // Produced is a subset of what was promised, so the promise segment
          // only draws the part not yet made. Stock overlaps neither — it was
          // never promised by a machine — so it is added, not subtracted.
          const pctP = pct(num(r.produced));
          const pctR = Math.max(0, pct(num(r.reserved)) - pctP);
          const pctS = Math.min(100 - pctP - pctR, pct(stock));

          return (
            <div>
              <div className="d-flex align-items-center gap-2">
                <ProgressBar style={{ height: 8, flex: 1, minWidth: 90 }}>
                  <ProgressBar variant="success" now={pctP} key="p" />
                  <ProgressBar variant="primary" now={pctR} key="r" />
                  <ProgressBar variant="info" now={pctS} key="s" />
                </ProgressBar>
                <span className="fs-10 text-nowrap fw-semibold">
                  {stock > 0 ? `${stock}s · ` : ''}
                  {r.reserved}r · {r.produced}p / {q}
                </span>
              </div>
              {stock > 0 && (
                <span
                  className="fs-10 text-info"
                  title={
                    r.stockSerials?.length
                      ? `From warehouse: ${r.stockSerials.join(', ')}`
                      : undefined
                  }
                >
                  {fmt(r.stockMeters)} m off the shelf
                  {r.stockSerials?.length
                    ? ` · ${r.stockSerials.length} drum${
                        r.stockSerials.length === 1 ? '' : 's'
                      }`
                    : ''}
                </span>
              )}
              {r.free > 0 && (
                <span className="fs-10 text-warning ms-2">{r.free} free</span>
              )}
            </div>
          );
        },
        meta: { headerProps: { style: { minWidth: 240 } } }
      },
      {
        id: 'producedAt',
        header: 'Produced',
        enableSorting: false,
        cell: ({ row: { original } }) => <TimeSince ts={finishedTs(original)} label="finished" />,
        meta: { headerProps: { style: { width: '10%' } } }
      },
      {
        accessorKey: 'dueDate',
        header: 'Due',
        cell: ({ row: { original } }) => (
          <span className="fs-10 text-muted white-space-nowrap">
            {original.dueDate ? String(original.dueDate).slice(0, 10) : '—'}
          </span>
        ),
        meta: { headerProps: { style: { width: '9%' }, className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        id: 'details',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <div className="text-end">
            <Button
              size="sm"
              variant="outline-primary"
              className="py-0 px-2 fs-10"
              title="Full details (production board)"
              onClick={() =>
                navigate(`/apps/sales-management/reservations/${original.orderId}`)
              }
            >
              <FontAwesomeIcon icon={faDiagramProject} className="me-1" />
              Details
            </Button>
          </div>
        ),
        meta: { headerProps: { style: { width: 90 }, className: 'text-end' }, cellProps: { className: 'text-end' } }
      }
    ],
    [navigate]
  );

// When the reserved drum came off the machine. Written by the warehouse bridge
// onto traceability; the fallbacks cover items that predate it. Accepts both
// epoch ms and ISO strings, since the two sources differ.
const finishedTs = it => {
  const v =
    it?.traceability?.producedEnd ??
    it?.traceability?.producedAt ??
    it?.producedEnd ??
    it?.producedAt ??
    it?.completedAt ??
    null;
  if (!v) return null;
  const n = Number(v);
  if (Number.isFinite(n) && n > 1e11) return n;
  const parsed = new Date(v).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const OrderReservations = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['order-reservations'],
    queryFn: () => axios.get(API_BASE).then(res => res.data?.orders || []),
    refetchInterval: 5000,
    refetchOnWindowFocus: false,
    staleTime: 3000
  });
  const orders = data || [];

  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // flatten items + attach derived fields (spec/status for sort/filter)
  const rows = useMemo(() => {
    const out = [];
    for (const o of orders)
      for (const it of o.items || [])
        out.push({
          ...it,
          orderId: o.orderId,
          orderCode: o.orderCode,
          client: o.client,
          dueDate: o.dueDate,
          itemLabel: specOf(it),
          statusLabel: statusOf(it).label,
          statusKey: statusOf(it).key
        });
    return out;
  }, [orders]);

  const clients = useMemo(
    () => [...new Set(rows.map(r => r.client).filter(Boolean))].sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    let rs = rows;
    if (clientFilter !== 'all') rs = rs.filter(r => r.client === clientFilter);
    if (statusFilter !== 'all')
      rs = rs.filter(r => r.statusKey === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rs = rs.filter(
        r =>
          (r.productLabel || '').toLowerCase().includes(q) ||
          (r.orderCode || '').toLowerCase().includes(q) ||
          (r.client || '').toLowerCase().includes(q)
      );
    }
    return rs;
  }, [rows, clientFilter, statusFilter, search]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (a, r) => ({
          needed: a.needed + num(r.qtyPlanned),
          reserved: a.reserved + num(r.reserved),
          produced: a.produced + num(r.produced),
          stock: a.stock + num(r.stockReserved),
          free: a.free + num(r.free)
        }),
        { needed: 0, reserved: 0, produced: 0, stock: 0, free: 0 }
      ),
    [filtered]
  );

  const tabItems = useMemo(() => {
    // Keyed off statusKey rather than the label: a status whose name has a
    // space in it ("From stock") turned the old label-derived lookup into an
    // undefined bucket, and the counter silently went NaN.
    const c = { free: 0, partial: 0, reserved: 0, stock: 0, produced: 0 };
    for (const r of rows) if (r.statusKey in c) c[r.statusKey] += 1;
    return [
      { label: 'All', value: 'all', count: rows.length },
      { label: 'Free', value: 'free', count: c.free },
      { label: 'Partial', value: 'partial', count: c.partial },
      { label: 'Reserved', value: 'reserved', count: c.reserved },
      { label: 'From stock', value: 'stock', count: c.stock },
      { label: 'Produced', value: 'produced', count: c.produced }
    ];
  }, [rows]);

  const columns = useColumns(navigate);
  const table = useAdvanceTable({
    data: filtered,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h3 className="mb-0">Order Reservations</h3>
          <div className="text-muted fs-9">
            What's reserved, taken from stock or already produced for each
            order, broken down by job
          </div>
        </div>
        <span className="fs-10 text-body-tertiary d-flex align-items-center gap-1">
          <span
            className="rounded-circle"
            style={{
              width: 8,
              height: 8,
              background: isFetching ? '#22c55e' : '#94a3b8',
              display: 'inline-block'
            }}
          />
          Live · 5s
        </span>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <SummaryTile label="Needed" value={totals.needed} bg="secondary" />
        <SummaryTile label="Reserved" value={totals.reserved} bg="primary" />
        <SummaryTile label="From stock" value={totals.stock} bg="info" />
        <SummaryTile label="Produced" value={totals.produced} bg="success" />
        <SummaryTile label="Free" value={totals.free} bg="warning" />
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : isError ? (
        <div className="text-danger py-3">Failed to load reservations.</div>
      ) : (
        <>
          <FilterTab
            tabItems={tabItems}
            activeKey={statusFilter}
            onSelect={setStatusFilter}
            className="mb-2"
          />
          <AdvanceTableProvider {...table}>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <Form.Select
                size="sm"
                style={{ width: 180 }}
                value={clientFilter}
                onChange={e => setClientFilter(e.target.value)}
              >
                <option value="all">All clients</option>
                {clients.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
              <SearchBox
                placeholder="Search product / order / client…"
                onChange={e => setSearch(e.target.value || '')}
              />
              <span className="text-muted fs-10 ms-auto">
                {filtered.length} items
              </span>
            </div>

            {filtered.length === 0 ? (
              <div className="text-muted py-5 text-center">
                No reservations to show.
              </div>
            ) : (
              <>
                <AdvanceTable
                  tableProps={{
                    className: 'phoenix-table border-top border-200 fs-9'
                  }}
                />
                <AdvanceTableFooter pagination />
              </>
            )}
          </AdvanceTableProvider>
        </>
      )}
    </div>
  );
};

export default OrderReservations;
